import React, { useCallback } from 'react'
import tw from 'twin.macro'
import { graphql } from 'gatsby'
import Card, { CardProps } from '@/components/ui/card'
import RichText from '@/components/ui/rich-text'
import DynamicAction, { DynamicActionProps } from '@/components/ui/dynamic-action'
import { RenderRichTextData, ContentfulRichTextGatsbyReference } from 'gatsby-source-contentful/rich-text'
import { slugify } from '@/utils/strings'
import colors from '@/constants/colors'
import { ExperienceMapper } from '@ninetailed/experience.js-utils'
import { Experience } from '@ninetailed/experience.js-gatsby'
import PricingCard, { PricingCardProps } from '@/components/ui/pricing-card'
import Image from '@/components/ui/image'
import Spinner from '@/icons/bwi/spinner.svg'
import CodeTabs from '@/components/ui/code-tabs'
import { extractDataAttributes } from '@/utils/dom'

export const fragment = graphql`
  fragment SectionSetOfCards on ContentfulSectionSetOfCards {
    __typename
    contentful_id
    internalName
    headline
    body {
      raw
      #  references {
      #    ...Link
      #  }
    }
    backgroundColor
    dropShadow
    unconstrained
    cards {
      ... on ContentfulComponentCard {
        ...ComponentCard
      }
      ... on ContentfulBlogPage {
        ...BlogCard
      }
      ... on ContentfulResourcePage {
        ...ResourcePage
        image {
          ...Asset
        }
      }
      ... on ContentfulResourceVideoPage {
        ...ResourceVideoPage
      }
      ... on ContentfulLandingPage {
        ...LandingPage
      }
      ... on ContentfulComponentPricing {
        ...ComponentPricing
      }
      ... on ContentfulComponentImage {
        ...ComponentImage
      }
      ... on ContentfulComponentCodeTabs {
        ...CodeTabs
      }
    }
    cardIconSize
    cardIconLocation
    stackImageOverText
    cardsPerColumn
    actions {
      ... on ContentfulComponentCtaLink {
        ...CtaLink
      }
      ... on ContentfulComponentHubspotCta {
        ...HubspotCTA
      }
    }
    actionsPosition
    excludeFromPdf
  }
`

// if this gets any more complicated just create a graphQL Query
interface CardWithTypename extends Queries.ContentfulComponentCard {
  __typename: 'ContentfulComponentCard'
}

interface LandingWithTypename extends Queries.ContentfulLandingPage {
  __typename: 'ContentfulLandingPage'
}

interface BlogWithTypename extends Queries.ContentfulBlogPage {
  __typename: 'ContentfulBlogPage'
}

interface ResourceWithTypename extends Queries.ContentfulResourcePage {
  __typename: 'ContentfulResourcePage'
}

interface ResourceVideoWithTypename extends Queries.ContentfulResourceVideoPage {
  __typename: 'ContentfulResourceVideoPage'
}

interface PricingWithTypename extends Queries.ContentfulComponentPricing {
  __typename: 'ContentfulComponentPricing'
}

interface ImageWithTypename extends Queries.ContentfulComponentImage {
  __typename: 'ContentfulComponentImage'
}

export type DynamicCard =
  | CardWithTypename
  | LandingWithTypename
  | BlogWithTypename
  | ResourceWithTypename
  | ResourceVideoWithTypename
  | PricingWithTypename
  | ImageWithTypename

export type SetOfCardsProps = {
  actions?: DynamicActionProps[]
  actionsPosition?: string
  backgroundColor?: string
  body?: RenderRichTextData<ContentfulRichTextGatsbyReference>
  cardIconLocation?: string
  cardIconSize?: string
  cards: DynamicCard[]
  cardsPerColumn?: number
  dropShadow?: boolean
  headline?: string
  internalName?: string
  isHero?: boolean
  pageColor?: string
  unconstrained?: boolean
  stackImageOverText?: boolean
  pageOrderNo?: number
  excludeFromPdf?: boolean
  isPrintView?: boolean
}

// eslint-disable-next-line react/function-component-definition

export default function SetOfCards(props): JSX.Element {
  const Loader = useCallback(
    () => (
      <div tw="w-full flex justify-center items-center p-8 h-[700px]">
        <Spinner tw="animate-spin h-20 w-20 text-gray-400" />
      </div>
    ),
    []
  )

  const mapExperiences = useCallback((experiences) => {
    if (!experiences || experiences.length === 0 || !experiences[0]?.variants) return []
    return experiences
      .map(({ variants, ...rest }) => ({
        ...rest,
        variants: variants.map((v) => ({ ...v, id: v.contentful_id, isHero: props.isHero })),
      }))
      .filter(ExperienceMapper.isExperienceEntry)
      .map(ExperienceMapper.mapExperience)
  }, [])

  return (
    <Experience
      {...props}
      id={props.contentful_id ?? props.id}
      key={props.contentful_id ?? props.id}
      experiences={mapExperiences(props.nt_experiences)}
      component={SetOfCardsSection}
      passthroughProps={{ ...props }}
      loadingComponent={Loader}
    />
  )
}

// eslint-disable-next-line react/function-component-definition
export function SetOfCardsSection({
  actions,
  actionsPosition,
  backgroundColor,
  body,
  cardIconLocation,
  cardIconSize,
  cards,
  cardsPerColumn,
  dropShadow,
  headline,
  internalName,
  isHero = false,
  pageColor,
  unconstrained,
  stackImageOverText = true,
  pageOrderNo,
  excludeFromPdf,
  isPrintView,
  ...rest
}: SetOfCardsProps): JSX.Element {
  const dataAttributes = extractDataAttributes(rest)
  // if cards per column is not explicitly set, as long as there are 6 or fewer cards, show all cards in a single row. Otherwise default to 3 per row.
  const columnCount = cardsPerColumn || (cards?.length <= 6 ? cards?.length : 3) || 1
  const { primaryBlue, lightGray, indigoBlue, white, transparent } = colors
  const HeadingWrap = useCallback(
    ({ children }) => (
      <>
        {pageOrderNo === 1 ? (
          <h2 tw="text-center font-normal h3" css={[isHero && tw`h1`]}>
            {children}
          </h2>
        ) : (
          <h3 tw="text-center font-normal" css={[isHero && tw`h1`]}>
            {children}
          </h3>
        )}
      </>
    ),
    []
  )

  const renderActions = () => (
    <div tw="flex flex-wrap justify-center gap-6 my-6">
      {actions.map((action) => (
        <DynamicAction key={action.id} {...action} backgroundColor={backgroundColor} data-testid="card-cta" />
      ))}
    </div>
  )

  return isPrintView ? (
    !excludeFromPdf && (
      <div tw="border-b-[0.25pt] py-3">
        {headline && <h2 css={['font-medium!', !body && tw`py-3`]}>{headline}</h2>}
        {body && (
          <div>
            <RichText body={body} />
          </div>
        )}
        <div
          css={[
            (cards?.length ?? 0) > 1 && tw`grid grid-cols-2 gap-x-6`,
            (cards?.length ?? 0) === 6 && tw`grid grid-cols-3 gap-x-6`,
            (cards?.length ?? 0) === 3 && tw`grid grid-cols-3 gap-x-4`,
          ]}
        >
          {cards?.map((card: any) => (
            <div css={[(cards?.length ?? 0) === 1 && tw`mt-3`]}>
              {card?.headline && (
                <h3 css={[tw`text-pretty font-medium!`, (cards?.length ?? 0) > 1 && tw`text-[10pt]!`]}>
                  {card.headline}
                </h3>
              )}
              {card?.subtext && (
                <div tw="[&_h4]:font-medium!">
                  <RichText body={card.subtext} />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    )
  ) : (
    <section
      id={slugify(internalName)}
      css={[
        isHero && unconstrained && tw`!mt-0`,
        unconstrained && backgroundColor === white && tw`bg-white`,
        unconstrained && backgroundColor === lightGray && tw`bg-gray-100`,
        unconstrained && backgroundColor === primaryBlue && tw`bg-primaryBlue`,
        unconstrained && backgroundColor === indigoBlue && tw`bg-indigoBlue`,
        unconstrained && backgroundColor === transparent && tw`bg-transparent`,
        tw`dark:(bg-transparent text-white)`,
      ]}
      {...dataAttributes}
    >
      {(headline || body) && (
        <div
          tw="max-w-4xl mx-auto text-indigoBlue px-5 mb-12 dark:text-white"
          css={[
            (backgroundColor === indigoBlue || backgroundColor === primaryBlue) && unconstrained && tw`text-white`,
            (pageColor === indigoBlue || pageColor === primaryBlue) && !unconstrained && tw`text-white`,
            backgroundColor !== pageColor && unconstrained && tw`pt-8 lg:pt-16`,
          ]}
        >
          {headline && <HeadingWrap>{headline}</HeadingWrap>}
          {body && (
            <div tw="text-center">
              <RichText body={body} />
              {actions && actionsPosition === 'Above cards' && renderActions()}
            </div>
          )}
        </div>
      )}
      <div>
        {cards && (
          <div
            className="set-of-cards-container"
            css={[
              tw`max-w-8xl mx-auto px-5 lg:px-9 grid gap-5 xl:(gap-12) grid-cols-1`,
              columnCount === 1 && tw`lg:grid-cols-1`,
              columnCount === 2 && tw`lg:grid-cols-2`,
              columnCount === 3 && tw`lg:grid-cols-3`,
              columnCount === 4 && tw`lg:grid-cols-4`,
              columnCount === 5 && tw`lg:grid-cols-5`,
              columnCount === 6 && tw`lg:grid-cols-6`,
              actions && tw`mb-10 lg:mb-20`,
            ]}
          >
            {cards.map((card, idx) => {
              let cardProps: CardProps = { headline: '' }
              if (card.__typename === 'ContentfulComponentCard') {
                const defaultCard = card
                cardProps = { ...defaultCard } as unknown as CardProps
              } else if (card.__typename === 'ContentfulBlogPage') {
                const blogCard = card as any
                cardProps = {
                  tagline: 'Blog',
                  headline: blogCard.title,
                  subtext: blogCard.description,
                  image: blogCard.image,
                  actions: [
                    {
                      id: blogCard.id,
                      to: `/blog/${blogCard.slug}`,
                    },
                  ],
                }
              } else if (card.__typename === 'ContentfulResourcePage') {
                const resourceCard = card
                cardProps = {
                  tagline: 'Resource',
                  headline: resourceCard.title,
                  subtext: resourceCard.description,
                  image: resourceCard.image || resourceCard.seo?.ogImage,
                  actions: [
                    {
                      __typename: 'CtaLink',
                      id: resourceCard.id,
                      to: `/resources/${resourceCard.slug}`,
                    },
                  ],
                }
              } else if (card.__typename === 'ContentfulResourceVideoPage') {
                const resourceVideoCard = card
                cardProps = {
                  tagline: 'Video',
                  headline: resourceVideoCard.title,
                  subtext: resourceVideoCard.image?.description,
                  image: resourceVideoCard.image || resourceVideoCard.seo?.ogImage,
                  actions: [
                    {
                      __typename: 'CtaLink',
                      id: resourceVideoCard.id,
                      to: `/resources/videos/${resourceVideoCard.slug}`,
                    },
                  ],
                }
              } else if (card.__typename === 'ContentfulLandingPage') {
                const pageCard = card
                cardProps = {
                  headline: pageCard?.seo?.title,
                  subtext: pageCard?.seo?.description?.description,
                  image: pageCard?.seo?.ogImage,
                  actions: [
                    {
                      __typename: 'CtaLink',
                      id: pageCard.id,
                      to: `/${pageCard.slug}`,
                    },
                  ],
                }
              } else if (card.__typename === 'ContentfulComponentPricing') {
                const { price, features, actions: pricingCardActions, ...pricingCard } = card
                const pricingCardProps: PricingCardProps = {
                  ...pricingCard,
                  price: Number(price),
                  features: features as string[],
                  actions: pricingCardActions as unknown as DynamicActionProps[],
                  hasShadow: true,
                }
                return <PricingCard key={pricingCard.id || card.contentful_id} {...pricingCardProps} />
              } else if ((card as any).__typename === 'ContentfulComponentCodeTabs') {
                return <CodeTabs key={card.contentful_id} code={(card as any).code} />
              } else if (card.__typename === 'ContentfulComponentImage') {
                return <Image key={card.id || card.contentful_id} {...card} />
              }
              return (
                <Card
                  key={`card-${idx}`}
                  {...cardProps}
                  {...{
                    backgroundColor,
                    dropShadow,
                    cardIconLocation:
                      columnCount === 1 && idx % 2 === 1
                        ? { Right: 'Left', Left: 'Right' }[cardIconLocation]
                        : cardIconLocation,
                    cardIconSize,
                    isHero,
                    columnCount,
                    unconstrained,
                    stackImageOverText,
                  }}
                />
              )
            })}
          </div>
        )}
        {actions && actionsPosition !== 'Above cards' && renderActions()}
      </div>
    </section>
  )
}
